import { Component, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core';
import { UserService } from '@http/clients/user.service';
import { OpportunityUser } from '@models/OpportunityUser';
import { SearchUsersParameters, UserView } from '@services/http/clients/api-proxies';
import { GenericMultipleSelectionComponent } from '@shared/generic-multiple-selection/generic-multiple-selection.component';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';

@Component({
    selector: 'app-salesperson-multi-select-editor',
    standalone: true,
    imports: [GenericMultipleSelectionComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './custom-cell-user-sales.component.html'
})
export class customCellUserSalesComponent implements ICellEditorAngularComp, OnDestroy {
    params!: ICellEditorParams;

    value: OpportunityUser[] = [];
    tempValue: { externalId?: number; fullName?: string }[] = [];
    allUsers: UserView[] = [];
    isLoading = signal<boolean>(true);
    isOpen = signal<boolean>(false);
    open = signal<boolean>(false);

    private readonly userService = inject(UserService);

    async agInit(params: ICellEditorParams): Promise<void> {
        this.params = params;

        const initialValue: OpportunityUser[] = params.data?.opportunityUsers ?? params.value ?? [];

        this.value = [...initialValue];

        this.tempValue = initialValue.map((u) => ({
            externalId: u.userExternalId,
            fullName: u.userFullName
        }));
        params.api.addEventListener('bodyScroll', this.bodyScrollListener);
        this.open.set(true);

        try {
            const results = await this.userService.search({ filterText: '' } as SearchUsersParameters);

            const selectedIds = new Set(this.tempValue.map((u) => u.externalId));

            this.allUsers = (results ?? []).filter((user) => user.isActive || selectedIds.has(user.externalId));
        } catch {
            this.allUsers = [];
        }

        this.isLoading.set(false);
    }

    ngOnDestroy(): void {
        if (this.params?.api) {
            this.params.api.removeEventListener('bodyScroll', this.bodyScrollListener);
        }
    }

    getValue(): OpportunityUser[] | null {
        return this.value;
    }

    onApply(selected: any[]): void {
        const existingMap = new Map(this.value.map((u) => [u.userExternalId, u]));
        this.value = selected.map((user) => existingMap.get(user.externalId) ?? OpportunityUser.fromUserView(user));

        if (this.params.data) {
            this.params.data.opportunityUsers = this.value;
        }

        this.params.api.stopEditing();
    }

    onCancel(): void {
        this.tempValue = this.value.map((u) => ({
            externalId: u.userExternalId,
            fullName: u.userFullName
        }));
        this.params.api.stopEditing(true);
    }

    isPopup(): boolean {
        return true;
    }

    private bodyScrollListener = () => this.onBodyScroll();

    private onBodyScroll(): void {
        this.isOpen.set(false);
        this.open.set(false);
        this.params.api.stopEditing(true);

        this.params.api.removeEventListener('bodyScroll', this.bodyScrollListener);
    }
}
